import React from 'react';
import useUnitContext, { useUnitProvider } from '/@/core/context/unit';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import type { UnitProviderProps } from '/@/views/visit-reports/edit/components/field-observation-section/components/precipitation-graphic';
import { useWeatherForecast } from '../frost-risk';
/**
 * @typedef {string | number} CropMonitoringId
 * */

/**
 * @param {string | number} cropMonitoringId
 * @returns {Promise<{data: HistoricalWeatherQuery}>}
 */
export const getHistoricalWeather = (cropMonitoringId: number, system: string, startDays?: number) => {
    const params = new URLSearchParams();
    params.append('system', system);
    if (startDays !== undefined) params.append('start_days', startDays.toString());
    return axFetch.get(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/historical-weather/?${params.toString()}`
    );
};

/**
 * @param {string | number} cropMonitoringId
 * @param {UnitProviderProps} unitsProps
 * @param {{startDays?: number, enabled?: boolean}} options
 *
 * `startDays` and `enabled` live in the same options object on purpose: a positional
 * `startDays` in front of the options is trivially passed the options object by mistake,
 * and the backend answers 500 to a non-numeric `start_days`.
 */
export const useGetHistoricalWeather = (
    cropMonitoringId: number,
    unitsProps: UnitProviderProps,
    { startDays, enabled = true }: { startDays?: number; enabled?: boolean } = {}
) => {
    /** @type {[HistoricalWeatherQuery | undefined, React.Dispatch<HistoricalWeatherQuery>]} */
    const [state, setState] = React.useState();
    // Start out loading whenever a fetch is going to happen, so consumers don't see a
    // "not loading / no data" frame before the effect below kicks the request off.
    const [loading, setLoading] = React.useState(Boolean(enabled && cropMonitoringId));
    const [error, setError] = React.useState();
    const units = useUnitContext(unitsProps);

    const fetch = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(undefined);
            const res = await getHistoricalWeather(cropMonitoringId, units.system, startDays);
            setState(res.data);
            return Promise.resolve(res.data);
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [cropMonitoringId, units.system, startDays]);

    React.useEffect(() => {
        enabled && cropMonitoringId && fetch();
    }, [enabled, cropMonitoringId, fetch]);

    return { data: state, loading, error };
};
/**
 * @typedef {Object} HistoricalWeatherQuery
 * @property {number} count
 * @property {HistoricalWeather[]} results
 */
/**
 * @typedef {Object} HistoricalWeather
 * @property {number} tempMax
 * @property {number} tempMin
 * @property {string} date
 * @property {number} gdu
 * @property {OtherData} otherData
 * @property {number} gduAccum
 * @property {number} precipAccum
 */
/**
 * @typedef {Object} OtherData
 * @property {string} date
 * @property {string} geoType
 * @property {string} geoTypeId
 * @property {string} latitude
 * @property {string} longitude
 * @property {string} PrecipAmountLocalAfternoonAvg
 * @property {string} PrecipAmountLocalAfternoonMax
 * @property {string} PrecipAmountLocalAfternoonMin
 * @property {string} PrecipAmountLocalDayAvg
 * @property {string} PrecipAmountLocalDayMax
 * @property {string} PrecipAmountLocalDayMin
 * @property {string} PrecipAmountLocalDaytimeAvg
 * @property {string} PrecipAmountLocalDaytimeMax
 * @property {string} PrecipAmountLocalDaytimeMin
 * @property {string} PrecipAmountLocalEveningAvg
 * @property {string} PrecipAmountLocalEveningMax
 * @property {string} PrecipAmountLocalEveningMin
 * @property {string} PrecipAmountLocalMorningAvg
 * @property {string} PrecipAmountLocalMorningMax
 * @property {string} PrecipAmountLocalMorningMin
 * @property {string} PrecipAmountLocalNighttimeAvg
 * @property {string} PrecipAmountLocalNighttimeMax
 * @property {string} PrecipAmountLocalNighttimeMin
 * @property {string} PrecipAmountLocalOvernightAvg
 * @property {string} PrecipAmountLocalOvernightMax
 * @property {string} PrecipAmountLocalOvernightMin
 * @property {string} RelativeHumidityLocalAfternoonAvg
 * @property {string} RelativeHumidityLocalAfternoonMax
 * @property {string} RelativeHumidityLocalAfternoonMin
 * @property {string} RelativeHumidityLocalDayAvg
 * @property {string} RelativeHumidityLocalDayMax
 * @property {string} RelativeHumidityLocalDayMin
 * @property {string} RelativeHumidityLocalDaytimeAvg
 * @property {string} RelativeHumidityLocalDaytimeMax
 * @property {string} RelativeHumidityLocalDaytimeMin
 * @property {string} RelativeHumidityLocalEveningAvg
 * @property {string} RelativeHumidityLocalEveningMax
 * @property {string} RelativeHumidityLocalEveningMin
 * @property {string} RelativeHumidityLocalMorningAvg
 * @property {string} RelativeHumidityLocalMorningMax
 * @property {string} RelativeHumidityLocalMorningMin
 * @property {string} RelativeHumidityLocalNighttimeAvg
 * @property {string} RelativeHumidityLocalNighttimeMax
 * @property {string} RelativeHumidityLocalNighttimeMin
 * @property {string} RelativeHumidityLocalOvernightAvg
 * @property {string} RelativeHumidityLocalOvernightMax
 * @property {string} RelativeHumidityLocalOvernightMin
 * @property {string} TemperatureLocalAfternoonAvg
 * @property {string} TemperatureLocalAfternoonMax
 * @property {string} TemperatureLocalAfternoonMin
 * @property {string} TemperatureLocalDayAvg
 * @property {string} TemperatureLocalDayMax
 * @property {string} TemperatureLocalDayMin
 * @property {string} TemperatureLocalDaytimeAvg
 * @property {string} TemperatureLocalDaytimeMax
 * @property {string} TemperatureLocalDaytimeMin
 * @property {string} TemperatureLocalEveningAvg
 * @property {string} TemperatureLocalEveningMax
 * @property {string} TemperatureLocalEveningMin
 * @property {string} TemperatureLocalMorningAvg
 * @property {string} TemperatureLocalMorningMax
 * @property {string} TemperatureLocalMorningMin
 * @property {string} TemperatureLocalNighttimeAvg
 * @property {string} TemperatureLocalNighttimeMax
 * @property {string} TemperatureLocalNighttimeMin
 * @property {string} TemperatureLocalOvernightAvg
 * @property {string} TemperatureLocalOvernightMax
 * @property {string} TemperatureLocalOvernightMin
 * @property {string} WindSpeedLocalAfternoonAvg
 * @property {string} WindSpeedLocalAfternoonMax
 * @property {string} WindSpeedLocalAfternoonMin
 * @property {string} WindSpeedLocalDayAvg
 * @property {string} WindSpeedLocalDayMax
 * @property {string} WindSpeedLocalDayMin
 * @property {string} WindSpeedLocalDaytimeAvg
 * @property {string} WindSpeedLocalDaytimeMax
 * @property {string} WindSpeedLocalDaytimeMin
 * @property {string} WindSpeedLocalEveningAvg
 * @property {string} WindSpeedLocalEveningMax
 * @property {string} WindSpeedLocalEveningMin
 * @property {string} WindSpeedLocalMorningAvg
 * @property {string} WindSpeedLocalMorningMax
 * @property {string} WindSpeedLocalMorningMin
 * @property {string} WindSpeedLocalNighttimeAvg
 * @property {string} WindSpeedLocalNighttimeMax
 * @property {string} WindSpeedLocalNighttimeMin
 * @property {string} WindSpeedLocalOvernightAvg
 * @property {string} WindSpeedLocalOvernightMax
 * @property {string} WindSpeedLocalOvernightMin
 * @property {number} PrecipAmountLocalDayAccum
 */

export const useForecastNotifications = ({ lat, lng }: { lat: number; lng: number }) => {
    const forecastWeatherQuery = useWeatherForecast({ lat, lng });
    const hourlyForecasts = forecastWeatherQuery.data?.data.hourlyForecasts || [];
    const units = useUnitProvider();
    let maxTemp: null | number = null;
    let minTemp: null | number = null;
    let maxWindSpeed: null | number = null;
    let maxRain: null | number = null;
    hourlyForecasts?.slice(0, 72).forEach((hour) => {
        const temp = hour['data']['temp'];
        const windSpeed = hour['data']['windSpeed'];
        const rain = hour['data']['rain'];

        if (maxTemp === null || temp > maxTemp) maxTemp = Math.round(temp * 100) / 100;
        if (minTemp === null || temp < minTemp) minTemp = Math.round(temp * 100) / 100;
        if (maxWindSpeed === null || windSpeed > maxWindSpeed) maxWindSpeed = Math.round(windSpeed * 100) / 100;
        if (maxRain === null || rain > maxRain) maxRain = Math.round(rain * 100) / 100;
    });

    const notifications = [];
    const maxTempThreshold = units.toTemperature(35, 0).value;
    const minTempThreshold = units.toTemperature(2, 0).value;
    if (maxTemp && maxTemp >= maxTempThreshold)
        notifications.push(
            _(
                `La temperatura se espera que supere los ${maxTemp}${units.getUnit('temperature')} en los próximos días. Riesgo de stress térmico.`,
                { maxTemp }
            )
        );
    if (minTemp && minTemp <= minTempThreshold)
        notifications.push(
            _(
                `Se prevé que la temperatura descienda por debajo de ${minTemp}${units.getUnit('temperature')} en los próximos días. Riesgo de heladas.`,
                { minTemp }
            )
        );
    if (maxWindSpeed && maxWindSpeed > 39)
        notifications.push(
            _(
                'Se espera que los vientos superen los {{maxWindSpeed}} km/h en los próximos días. Riesgo de vuelco o quebrado.',
                { maxWindSpeed }
            )
        );
    if (maxRain && maxRain > 50)
        notifications.push(_('Hay una alta probabilidad de lluvia ({{maxRain}}%) en los próximos días.', { maxRain }));

    return notifications;
};
